import type { ToasterProps } from 'sonner';
import {
  CircleAlertIcon,
  CircleCheckBigIcon,
  CircleXIcon,
  InfoIcon,
} from 'src/components/media-and-icons/icons';
import { cn } from 'src/utils/cn';

const isTestRunner = window.navigator.userAgent.match(/StorybookTestRunner/);

// We store here the global config of sonner, which then can be passed onto both providers, the app and the mock ones.
// see https://sonner.emilkowal.ski/toaster#api-reference
export const sonnerConfig: ToasterProps = {
  expand: true,
  duration: 5000,
  position: 'top-right',
  closeButton: true,
  richColors: false,
  toastOptions: {
    unstyled: true,
    classNames: {
      toast: cn(
        `
          relative flex gap-lg rounded-xs border-l-6 px-xl py-lg
          shadow-elevation-sm
          not-legacy:bg-background-surface-default not-legacy:md:w-[340px]
        `,
        `
          legacy:w-[350px] legacy:rounded-none legacy:border-none
          legacy:p-[1.5rem] legacy:text-white legacy:shadow-none
        `,
        // Disable transitions for the toasts when in test runner mode in order to have stable image regression tests
        isTestRunner && 'transition-none!'
      ),
      content: cn('mr-2xl flex flex-col gap-xs'),
      title: cn(
        `
          flex min-h-[24px] flex-col justify-center text-body-base
          [&:has(+[data-description])]:font-body-semibold
        `,
        `
          legacy:min-h-auto legacy:text-legacy-base
          legacy:[&:has(+[data-description])]:font-legacy-bold
        `
      ),
      icon: cn(`legacy:hidden`),
      description: cn(
        `text-body-base text-foreground-surface-subdued`,
        `legacy:text-legacy-base legacy:text-white`
      ),
      closeButton: cn(
        `
          absolute top-md right-xl min-h-[28px] cursor-pointer
          text-foreground-surface-subdued
          hover:text-foreground-surface-default
          [&>svg]:h-[16px] [&>svg]:w-[16px]
        `,
        `
          legacy:top-xl legacy:min-h-auto legacy:text-white
          legacy:hover:text-white
        `
      ),
      info: cn(
        `border-border-support-info-default`,
        `legacy:bg-legacy-brand-light-1`
      ),
      success: cn(
        `border-border-support-success-default`,
        `legacy:bg-[#5cb85c]/80`
      ),
      warning: cn(
        `border-border-support-warning-default`,
        `legacy:bg-legacy-warning-light legacy:text-[#8a6d3b]!`
      ),
      error: cn(
        `border-border-support-error-default`,
        `legacy:bg-legacy-error/80`
      ),
    },
  },
  icons: {
    info: <InfoIcon size="md" className={cn(`text-foreground-support-info`)} />,
    success: (
      <CircleCheckBigIcon
        size="md"
        className={cn(`text-foreground-support-success`)}
      />
    ),
    warning: (
      <CircleAlertIcon
        size="md"
        className={cn(`text-foreground-support-warning`)}
      />
    ),
    error: (
      <CircleXIcon size="md" className={cn(`text-foreground-support-error`)} />
    ),
  },
};
